import { END_POINTS } from "../constants/urls";
import emptySplitApi from "../utils/rtk";
import type {
  ApiResponse,
  TransactionListData,
  UserTransactionsQueryParams,
} from "../types/Transaction";

interface CommonResponseType {
  statusCode: number;
  message: string;
}

export const userApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    addUser: builder.mutation<CommonResponseType, any>({
      query: (body) => ({
        url: END_POINTS.addUser,
        method: "POST",
        body,
      }),
      invalidatesTags: ["USER"],
    }),
    getUserList: builder.query<
      CommonResponseType & {
        data: {
          list: any[];
          totalCount: number;
        };
      },
      {
        page: number;
        limit: number;
        search?: string;
        isBlocked?: string;
        isDeleted?: string;
        userType?: string;
        dateFrom?: string;
        dateTo?: string;
        role?: string;
      }
    >({
      query: ({
        page,
        limit,
        search,
        isBlocked,
        isDeleted,
        userType,
        dateFrom,
        dateTo,
        role,
      }) => ({
        url: `${END_POINTS.users}?page=${page}&limit=${limit}${
          search ? `&search=${search}` : ""
        }${
          isBlocked !== "0"
            ? `&isBlocked=${isBlocked === "2" ? true : false}`
            : ""
        }${userType && userType !== "0" ? `&userType=${userType}` : ""}${
          isDeleted !== "0"
            ? `&isDeleted=${isDeleted === "3" ? true : false}`
            : ""
        }${dateFrom ? `&dateFrom=${dateFrom}` : ""}${
          dateTo ? `&dateTo=${dateTo}` : ""
        }${role ? `&role=${role}` : ""}`,
        method: "GET",
      }),
      providesTags: ["USER"],
    }),
    getUsersById: builder.query<
      CommonResponseType & { data: any },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${END_POINTS.addUser}/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, { id }) => [{ type: "USER", id }],
    }),
    exportUsers: builder.query<
      CommonResponseType & { data: any },
      { search?: string; isDeleted?: string; userType?: string; role?: string }
    >({
      query: ({ search, isDeleted, userType, role }) => {
        const includeIsDeleted = isDeleted === "1" || isDeleted === "3";
        const isDeletedValue = isDeleted === "3" ? true : false; // '1' => false, '2' => false, '3' => true (deleted)

        const parts: string[] = [];
        if (search) parts.push(`search=${search}`);
        if (includeIsDeleted) parts.push(`isDeleted=${isDeletedValue}`);
        if (userType && userType !== "0") parts.push(`userType=${userType}`);
        if (role && role !== "0") parts.push(`role=${role}`);

        const qs = parts.join("&");
        return {
          url: `${END_POINTS.usersExport}${qs ? `?${qs}` : ""}`,
          method: "GET",
        };
      },
    }),
    getUserTransactions: builder.query<
      ApiResponse<TransactionListData>,
      UserTransactionsQueryParams
    >({
      query: ({
        id,
        search = "",
        page = 1,
        limit = 10,
        dateFrom = "",
        dateTo = "",
      }) => {
        const params: string[] = [
          `userId=${id}`,
          `page=${page}`,
          `limit=${limit}`,
        ];

        if (search) {
          params.push(`search=${encodeURIComponent(search)}`);
        }
        if (dateFrom) {
          params.push(`dateFrom=${encodeURIComponent(dateFrom)}`);
        }
        if (dateTo) {
          params.push(`dateTo=${encodeURIComponent(dateTo)}`);
        }

        return {
          url: `${END_POINTS.usersTransactions}/?${params.join("&")}`,
          method: "GET",
        };
      },
    }),
    updateUsersById: builder.mutation<
      CommonResponseType & { data: any },
      { body: any; id: string }
    >({
      query: ({ body, id }) => ({
        url: `${END_POINTS.addUser}/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "USER", id },
        "USER",
      ],
    }),
    deleteUsersById: builder.mutation<
      CommonResponseType & { data: any },
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${END_POINTS.addUser}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "USER", id },
        "USER",
      ],
    }),
  }),
});

export const {
  useLazyGetUserListQuery,
  useAddUserMutation,
  useGetUserListQuery,
  useGetUsersByIdQuery,
  useLazyExportUsersQuery,
  useGetUserTransactionsQuery,
  useLazyGetUserTransactionsQuery,
  useUpdateUsersByIdMutation,
  useDeleteUsersByIdMutation,
} = userApi;
